import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import Icon from '../../components/AppIcon';
import api from '../../api/axios';

const STATUS_STYLES = {
  ready: 'bg-green-50 text-green-700 border border-green-200',
  processing: 'bg-blue-50 text-blue-700 border border-blue-200',
  pending_processing: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
  failed: 'bg-red-50 text-red-700 border border-red-200',
};

const STATUS_LABELS = {
  ready: 'Ready',
  processing: 'Processing',
  pending_processing: 'Pending',
  failed: 'Failed',
};

const SOURCE_DISPLAY = {
  csv_upload: 'CSV Upload',
  shopify: 'Shopify',
  api: 'API',
  crm: 'CRM',
  manual: 'Manual Entry',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const AudienceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [audience, setAudience] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');

  const fetchAudience = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/v1/audience/${id}`);
      setAudience(data?.data);
    } catch (err) {
      console.error('Failed to fetch audience:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchContacts = useCallback(async () => {
    try {
      setContactsLoading(true);
      const { data } = await api.get(`/api/v1/audience/${id}/contacts`, {
        params: { page: currentPage - 1, size: 50 },
      });
      setContacts(data?.data || []);
      setTotalPages(data?.pagination?.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    } finally {
      setContactsLoading(false);
    }
  }, [id, currentPage]);

  useEffect(() => {
    fetchAudience();
  }, [fetchAudience]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Poll while processing
  useEffect(() => {
    if (audience?.status !== 'processing' && audience?.status !== 'pending_processing') return;
    const interval = setInterval(() => {
      fetchAudience();
      fetchContacts();
    }, 5000);
    return () => clearInterval(interval);
  }, [audience?.status, fetchAudience, fetchContacts]);

  const filtered = contacts?.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      c?.name?.toLowerCase()?.includes(s) ||
      c?.phone?.toLowerCase()?.includes(s) ||
      c?.email?.toLowerCase()?.includes(s)
    );
  });

  if (loading) {
    return (
      <>
        <PrimaryNavigation />
        <div className="main-content min-h-screen bg-background flex items-center justify-center">
          <div className="flex items-center gap-3 text-gray-500">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
            <span className="text-sm">Loading audience...</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PrimaryNavigation />
      <div className="main-content min-h-screen bg-background flex flex-col">
        <div className="px-4 md:px-6 py-6 md:py-8 flex-1">
          {/* Back + Title */}
          <div className="mb-6 flex items-center gap-3">
            <button
              onClick={() => navigate('/audience')}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
            >
              <Icon name="ArrowLeft" size={18} />
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-gray-900">{audience?.name || 'Audience'}</h1>
              <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-2">
                <span>ID #{id}</span>
                <span>·</span>
                <span>{audience?.validCount?.toLocaleString('en-IN') || 0} Contacts</span>
                <span>·</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES?.[audience?.status] || 'bg-gray-100'}`}>
                  {STATUS_LABELS?.[audience?.status] || audience?.status}
                </span>
              </p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {[
              { label: 'Total Rows', value: audience?.totalRows || 0, icon: 'FileText' },
              { label: 'Valid Contacts', value: audience?.validCount || 0, icon: 'UserCheck' },
              { label: 'Invalid', value: audience?.invalidCount || 0, icon: 'UserX' },
              { label: 'Source', value: SOURCE_DISPLAY?.[audience?.source] || audience?.source || '—', icon: 'Database', isText: true },
            ].map((stat) => (
              <div key={stat.label} className="bg-white rounded-xl border border-[#E5E7EB] px-4 py-3">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name={stat.icon} size={14} className="text-gray-400" />
                  <span className="text-xs text-gray-500">{stat.label}</span>
                </div>
                <p className="text-xl font-bold text-gray-900">
                  {stat.isText ? stat.value : stat.value.toLocaleString('en-IN')}
                </p>
              </div>
            ))}
          </div>

          {/* Processing Progress */}
          {(audience?.status === 'processing' || audience?.status === 'pending_processing') && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-3 mb-5 flex items-center gap-3">
              <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800">Processing CSV...</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-1.5 bg-blue-200 rounded-full overflow-hidden max-w-[200px]">
                    <div
                      className="h-full bg-blue-600 rounded-full transition-all"
                      style={{ width: `${audience?.totalRows > 0 ? Math.round((audience?.processedRows / audience?.totalRows) * 100) : 0}%` }}
                    />
                  </div>
                  <span className="text-xs text-blue-600">
                    {audience?.processedRows || 0}/{audience?.totalRows || '?'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {audience?.status === 'failed' && audience?.errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-3 mb-5 flex items-center gap-3">
              <Icon name="AlertCircle" size={16} className="text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">{audience?.errorMessage}</p>
            </div>
          )}

          {/* Info Row */}
          <div className="flex items-center gap-4 mb-5 text-sm text-gray-500">
            <span className="flex items-center gap-1.5">
              <Icon name="Calendar" size={14} />
              Created {formatDate(audience?.createdAt)}
            </span>
            {audience?.usedInCampaigns > 0 && (
              <span className="flex items-center gap-1.5">
                <Icon name="Megaphone" size={14} />
                Used in {audience?.usedInCampaigns} campaign{audience?.usedInCampaigns > 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Search */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-xs">
              <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search contacts..."
                value={search}
                onChange={(e) => setSearch(e?.target?.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 placeholder-gray-400"
              />
            </div>
          </div>

          {/* Contacts Table */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/60">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">#</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Name</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Phone</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Email</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Valid</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {contactsLoading ? (
                    <tr>
                      <td colSpan={5} className="text-center py-12 text-gray-400">
                        <div className="flex items-center justify-center gap-3">
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                          <span>Loading contacts...</span>
                        </div>
                      </td>
                    </tr>
                  ) : filtered?.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-12 text-gray-400">
                        {search ? 'No contacts match your search' : 'No contacts found'}
                      </td>
                    </tr>
                  ) : (
                    filtered?.map((contact, idx) => (
                      <tr
                        key={contact?.id}
                        className="hover:bg-gray-50/70 transition-colors"
                      >
                        <td className="px-5 py-3.5 text-gray-400 text-xs tabular-nums">
                          {(currentPage - 1) * 50 + idx + 1}
                        </td>
                        <td className="px-4 py-3.5 font-medium text-gray-900 whitespace-nowrap">
                          {contact?.name || '—'}
                        </td>
                        <td className="px-4 py-3.5 text-gray-600 whitespace-nowrap">
                          {contact?.phone || '—'}
                        </td>
                        <td className="px-4 py-3.5 text-gray-600 whitespace-nowrap">
                          {contact?.email || '—'}
                        </td>
                        <td className="px-4 py-3.5 text-center">
                          {contact?.isValid !== false ? (
                            <span className="inline-flex items-center gap-1 text-green-600">
                              <Icon name="CheckCircle" size={14} />
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-400">
                              <Icon name="XCircle" size={14} />
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50/40">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed text-gray-500 transition-colors"
                >
                  <Icon name="ChevronLeft" size={16} />
                </button>
                <span className="text-sm text-gray-500">Page {currentPage} of {totalPages}</span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed text-gray-500 transition-colors"
                >
                  <Icon name="ChevronRight" size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AudienceDetail;
